import { useFeatureFlagEnabled } from '@posthog/react';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { isMobile } from 'react-device-detect';
import { getCartIdAtom } from '../store';
import { getCookie } from '../utils';
import { useCurrentUser } from './queries/useCurrentUser';

type ReviewCTAState =
  | {
      completed: boolean;
      shownAt?: number;
    }
  | undefined;

export const useGetFeedback = () => {
  const [cartId] = useAtom(getCartIdAtom);
  const { user } = useCurrentUser();
  const isFeedbackRequestEnabled = useFeatureFlagEnabled('feedback-request');

  /**
   * Cooldown periods:
   *  'completed'  — never show again (user submitted feedback or left a review)
   *  'interacted' — 30 days (user clicked thumbs up/down but didn't finish)
   *  'downloaded' — 30 days (user downloaded the extension from the happy path)
   *  'dismissed'  — 30 days (user closed the modal without interacting)
   *
   * Between sessions, the modal shows at most once every 7 days (see shouldReviewBeShown).
   */
  const hideReviewCTA = (
    status: 'completed' | 'interacted' | 'downloaded' | 'dismissed',
  ) => {
    const reviewState: ReviewCTAState = {
      completed: false,
    };

    if (status === 'completed') {
      reviewState.completed = true;
    } else if (status === 'interacted') {
      reviewState.shownAt = dayjs().add(30, 'day').valueOf();
    } else if (status === 'downloaded' || status === 'dismissed') {
      reviewState.shownAt = dayjs().add(30, 'day').valueOf();
    }

    localStorage.setItem('feedbackReview', JSON.stringify(reviewState));
  };

  const isFirstShow = (): boolean => {
    if (user) {
      return user.carts.length <= 1;
    }

    const cookieCarts = getCookie('cookieCarts');
    if (!cookieCarts) return true;

    try {
      const carts = JSON.parse(decodeURIComponent(cookieCarts));
      return carts.length <= 1;
    } catch {
      return true;
    }
  };

  const shouldReviewBeShown = (): boolean => {
    // https://github.com/duskload/react-device-detect/blob/HEAD/docs/selectors.md
    if (isMobile || cartId === 'AKGUX') {
      return false;
    }

    if ((process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') && !isFeedbackRequestEnabled) {
      return false;
    }

    const reviewLocalStorageString = localStorage.getItem('feedbackReview');
    const reviewState: ReviewCTAState = reviewLocalStorageString
      ? JSON.parse(reviewLocalStorageString)
      : undefined;

    // User hasn't seen the form. Show it.
    if (!reviewState) {
      localStorage.setItem(
        'feedbackReview',
        JSON.stringify({ completed: false, shownAt: dayjs().valueOf() }),
      );

      return true;
    }

    // User has completed the review form. Don't show again
    if (reviewState.completed === true) {
      return false;
    }

    // By now, we know the user hasn't completed the form but they have seen it before.
    // We need to decide if we should show it now or not.
    if (reviewState.shownAt) {
      const diffHours = dayjs().diff(dayjs(reviewState.shownAt), 'hour', true);

      if (diffHours > 24 * 7) {
        localStorage.setItem(
          'feedbackReview',
          JSON.stringify({ completed: false, shownAt: dayjs().valueOf() }),
        );

        return true;
      } else {
        return false;
      }
    } else {
      console.error('showReviewCTA() shownAt is missing from reviewState');

      localStorage.setItem(
        'feedbackReview',
        JSON.stringify({ completed: false, shownAt: dayjs().valueOf() }),
      );

      return true;
    }
  };

  return { shouldReviewBeShown, hideReviewCTA, isFirstShow };
};
